import { usesCommerceProjection } from './projectionScopes'
import { NotificationSettings } from '../notifications/NotificationSettings'
import { FeedbackBridge } from '../notifications/FeedbackBridge'
import { AppNotificationBanner } from '../notifications/AppNotificationBanner'
import { useUnreadThreadCount } from './unreadMessages'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { CartFab, MobileShell } from '../../components/chow'
import { isEligibleBasketOrder, useReservationBasketIds } from './reservationBasket'
import { MessagesFab } from '../../components/chow/CartFab'
import { PageTransition } from '../../styles/PageTransition'
import {
  prototypeStoreRegistry,
  useCartItemSnapshot,
  useMarketplaceOrderSnapshot,
  useProfileWalletBalanceSnapshot,
  useWalletTransactionSnapshot,
} from '../../generated/prototypeRuntime'
import { customerCartRecords } from './cart'
import { ChooseChowProvider, useChooseChow } from './ChooseChowContext'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { isLiveRuntime } from '../../lib/runtime'
import { isNativeShell } from '../../lib/shell'
import { useRouteMeta } from '../../lib/seoHead'
import { createPrototypeRuntimeContext } from '../../stores/prototype'
import { publishProjectionStatus } from '../../lib/projectionHealth'
import {
  expectLiveRuntime,
  resetActivePrototypeRuntime,
  setActivePrototypeRuntime,
} from '../../stores/activeRuntime'
import { AuthPage } from './auth/AuthPage'
import { DeleteAccountPage, KitchenGuidelinesPage, PrivacyPage, TermsPage } from './LegalPages'
import { ChefPromotionsPage } from '../promotions/PromotionsPage'
import { OpsApp } from '../ops/OpsApp'
import { LandingPage } from './LandingPage'
import { ChefDispatchPage, ChefHomePage, ChefOrderDetailPage } from './ChefKitchen'
import { ChefSchedulePage } from './ChefSchedulePage'
import { ChefWelcomePage } from './ChefWelcomePage'
import { ChefVerificationPage } from './ChefVerificationPage'
import { ChefMenuHub } from './ChefMenuHub'
import { MenuBuilderPage } from './MenuBuilderPage'
import { HomePage } from './HomePage'
import { useHydrateTrendingFeed } from './trending'
import { ChefsPage } from './ChefsPage'
import { KitchenPage } from './KitchenPage'
import { CartErrorToast } from './CartErrorToast'
import { DishesPage } from './DishesPage'
import { MenusPage } from './MenusPage'
import { DishDetailPage } from './DishDetailPage'
import { MealCalendarPage } from './MealCalendarPage'
import { AllergiesPage } from './AllergiesPage'
import { OrdersPage } from './OrdersPage'
import { WalletPage } from './WalletPage'
import { AccountDeletionPage, ProfilePage } from './ProfilePage'
import { SubscriptionsPage } from './SubscriptionsPage'
import { PublicPlansPage } from './PublicPlansPage'
import { InboxPage, HelpPage, CartPage } from './stubs'
import { KitchenMembershipsPage } from './KitchenMembershipsPage'
import { PlanDetailsPage } from './PlanDetailsPage'
import { KitchenBoardPage } from './KitchenBoardPage'

/** Send signed-in users straight to their dashboard; anonymous users land. */
const LandingGate = () => {
  const { status } = useAuth()
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />
  // The marketing landing exists to convert a stranger on the web. Inside the
  // installed app that conversion already happened, so it is just a wall
  // between the user and their account — open on sign-in instead.
  return isNativeShell() ? <Navigate to="/login" replace /> : <LandingPage />
}

/** `/chefs` was a second name for the kitchen list. Keep any filter it carried. */
const KitchensRedirect = () => {
  const { search } = useLocation()
  return <Navigate to={`/kitchens${search}`} replace />
}

/** `/kitchens/:id` was a second name for a kitchen page. */
const KitchenRedirect = () => {
  const { kitchenId = '' } = useParams()
  const { search } = useLocation()
  return <Navigate to={`/kitchen/${encodeURIComponent(kitchenId)}${search}`} replace />
}

/** Guard the authenticated app — bounce anonymous visitors to the landing page unless browsing public catalog routes. */
const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth()
  const location = useLocation()
  const isPublicCatalogRoute = (path: string) =>
    path === '/kitchens' ||
    path.startsWith('/kitchens/') ||
    path === '/kitchen' ||
    path.startsWith('/kitchen/') ||
    path === '/chefs' ||
    path.startsWith('/chefs/') ||
    path === '/dishes' ||
    path.startsWith('/dishes/') ||
    path === '/dish' ||
    path.startsWith('/dish/') ||
    path === '/menus' ||
    path.startsWith('/menus/') ||
    path === '/guidelines' ||
    path.startsWith('/guidelines/') ||
    path === '/kitchen-guidelines' ||
    path.startsWith('/kitchen-guidelines/')

  return status === 'authenticated' || isPublicCatalogRoute(location.pathname) ? (
    <>{children}</>
  ) : (
    <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />
  )
}

const isJWT = (token: string | null): token is string =>
  Boolean(token && token.split('.').length === 3)

/**
 * The organization claim from a JWT. This MUST scope the live projection
 * runtime: the server scopes every snapshot/delta to the authenticated
 * organization (projectiongw.SecurityTenantFunc), stamping each record with
 * that org. The client's read model drops any record whose tenant doesn't match
 * its scope tenant (see mapMutation in runtime-transport). So if the runtime
 * used the default placeholder tenant, every real record would be filtered out
 * and the app would render empty despite the data arriving. The tenant is the
 * organization.
 */
const orgFromToken = (token: string): string => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { organization_id?: string }
    return String(payload.organization_id ?? '').trim()
  } catch {
    return ''
  }
}

export const ProjectionRuntimeBridge = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth()
  const [, refresh] = useState(0)
  useEffect(() => {
    const changed = () => refresh((value) => value + 1)
    window.addEventListener('chow-session-change', changed)
    window.addEventListener('storage', changed)
    return () => {
      window.removeEventListener('chow-session-change', changed)
      window.removeEventListener('storage', changed)
    }
  }, [])
  const token = localStorage.getItem('auth_token')
  const hasOperator = Boolean(localStorage.getItem('ops_session'))
  // Declared during render, before any child reads a store: the effect below
  // swaps the runtime only after the first paint of the children.
  expectLiveRuntime((status === 'authenticated' || hasOperator) && isJWT(token))

  useEffect(() => {
    if ((status !== 'authenticated' && !hasOperator) || !isJWT(token)) {
      resetActivePrototypeRuntime()
      return undefined
    }
    const org = orgFromToken(token)
    const runtime = createPrototypeRuntimeContext({
      // Scope the read model to the authenticated organization so live records
      // (stamped with that org by the server) are kept, not filtered out.
      tenantId: org || undefined,
      dummyRecordCount: 0,
      live: true,
      autoConnect: false,
      projectionHeaders: () => ({ Authorization: `Bearer ${token}` }),
      projectionQuery: () => ({ access_token: token }),
      // Report the delta stream's health instead of dropping it: without this
      // a socket that gives up leaves every screen rendering its last snapshot
      // with nothing to say so. See lib/projectionHealth.
      onProjectionStatus: publishProjectionStatus,
    })
    if (!hasOperator) {
      const selected = runtime.liveBindings.filter((binding) => {
        const scope = binding.scope()
        return usesCommerceProjection(scope.domain, scope.collection)
      })
      runtime.liveBindings.splice(0, runtime.liveBindings.length, ...selected)
    }
    setActivePrototypeRuntime(runtime)
    runtime.liveBindings.forEach((binding) => { void binding.connect() })
    return () => {
      runtime.liveBindings.forEach((binding) => binding.disconnect())
    }
  }, [status, token, hasOperator])

  return <>{children}</>
}

/**
 * The authenticated app shell — runs inside the providers so it can pivot
 * navigation and routes on the active role. A customer gets the browse surface +
 * cart; a chef gets kitchen management (queue/dispatch + menu curation), no cart.
 * Home for both roles is `/dashboard`.
 */
const ChooseChowShell = () => {
  const location = useLocation()
  const { isChef, tabs, profileId } = useChooseChow()

  // Mount the commerce stores once at the app level so cart / orders / wallet
  // exist for the whole session — "Add" works before Cart is ever opened, and
  // the dock cart badge stays live regardless of which tab created the items.
  const cart = useCartItemSnapshot() as { records: readonly Record<string, unknown>[] }
  const orders = useMarketplaceOrderSnapshot() as { records: readonly Record<string, unknown>[] }
  const reservationIds = useReservationBasketIds(profileId)
  useProfileWalletBalanceSnapshot()
  useWalletTransactionSnapshot()
  useHydrateTrendingFeed()

  // The prototype runtime seeds every collection with dummy rows; cart must start empty so they reflect only the customer's actions.
  const reset = useRef(false)
  useEffect(() => {
    if (isLiveRuntime()) return
    if (reset.current) return
    reset.current = true
    const clear = (key: string) =>
      (
        prototypeStoreRegistry.get(key) as TenantProjectionStore<Record<string, unknown>> | undefined
      )?.replace([])
    clear('marketplace.carts')
  }, [profileId])

  const cartCount = useMemo(() => {
    const scoped = customerCartRecords(cart.records, profileId, isLiveRuntime())
    const selected = new Set(reservationIds)
    return scoped.reduce((sum, item) => sum + Number(item.quantity ?? 1), 0) +
      orders.records.filter(order => selected.has(String(order.id ?? '')) &&
        String(order.customer_profile_id ?? '') === profileId && isEligibleBasketOrder(order)).length
  }, [cart.records, profileId, orders.records, reservationIds])

  const unreadMessages = useUnreadThreadCount(profileId, isChef ? 'chef' : 'customer')
  const badges = useMemo(() => {
    const map: Record<string, number> = {}
    if (unreadMessages > 0) {
      map['/inbox'] = unreadMessages
      if (isChef) map['/orders'] = unreadMessages
    }
    return map
  }, [unreadMessages, isChef])

  return (
    <MobileShell
      badges={badges}
      tabs={tabs}
      cartCount={cartCount}
      showCart={!isChef}
      floatingAction={<><MessagesFab count={unreadMessages} />{!isChef && <CartFab count={cartCount} />}</>}
    >
      {/* No AnimatePresence around the routed view. `mode="wait"` kept the
          OUTGOING page mounted until its exit animation reported completion,
          and that report comes from requestAnimationFrame — which does not run
          while the document is hidden. A navigation in a backgrounded tab moved
          the URL and left the previous page on screen. Which page is mounted is
          not an animation's decision to make; PageTransition still animates the
          new page in, transform-only, so a stalled clock costs a slide and
          never a page. */}
      <PageTransition key={location.pathname} fill={location.pathname === "/inbox"}>
        {isChef ? (
          <Routes location={location}>
            <Route path="/dashboard" element={<ChefHomePage />} />
            <Route path="/kitchen-board" element={<KitchenBoardPage />} />
            <Route path="/schedule" element={<ChefSchedulePage />} />
            <Route path="/welcome" element={<ChefWelcomePage />} />
            <Route path="/promotions" element={<ChefPromotionsPage />} />
            <Route path="/verification" element={<ChefVerificationPage />} />
            <Route path="/orders" element={<ChefDispatchPage />} />
            <Route path="/orders/:orderId" element={<ChefOrderDetailPage />} />
            <Route path="/menu" element={<ChefMenuHub />} />
            <Route path="/menu/new" element={<MenuBuilderPage />} />
            <Route path="/menu/:menuId/edit" element={<MenuBuilderPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/accounts" element={<Navigate to="/wallet?tab=methods" replace />} />
            <Route path="/account" element={<Navigate to="/wallet?tab=methods" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationSettings key={profileId} />} />
            <Route path="/account/delete" element={<AccountDeletionPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        ) : (
          <Routes location={location}>
            <Route path="/dashboard" element={<HomePage />} />
            {/* One list (/kitchens) and one page per kitchen (/kitchen/:slug,
                the path the SEO service renders). The other spellings are
                kept only as redirects so old links and shares still land. */}
            <Route path="/kitchens" element={<ChefsPage />} />
            <Route path="/kitchen/:kitchenId" element={<KitchenPage />} />
            <Route path="/chefs" element={<KitchensRedirect />} />
            <Route path="/kitchens/:kitchenId" element={<KitchenRedirect />} />
            <Route path="/dishes" element={<DishesPage />} />
            <Route path="/menus" element={<MenusPage />} />
            <Route path="/dishes/:dishId" element={<DishDetailPage />} />
            <Route path="/dish/:dishId" element={<DishDetailPage />} />
            <Route path="/market" element={<Navigate to="/kitchens" replace />} />
            <Route path="/market/*" element={<Navigate to="/kitchens" replace />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationSettings key={profileId} />} />
            <Route path="/account/delete" element={<AccountDeletionPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/mealplan" element={<MealCalendarPage />} />
            <Route path="/allergies" element={<AllergiesPage />} />
            <Route path="/memberships" element={<KitchenMembershipsPage />} />
            <Route path="/plan/:type/:id" element={<PlanDetailsPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        )}
      </PageTransition>
      <FeedbackBridge key={`${profileId}:${isChef}`} />
      <AppNotificationBanner />
      {!isChef && <CartErrorToast />}
    </MobileShell>
  )
}

/**
 * The ChooseChow app: actor context + auth session wrapping the public landing /
 * auth screens and the gated, role-aware dashboard shell.
 */
export const ChooseChowApp = () => {
  // Keeps <title>, the description and the canonical honest across client
  // navigation. The build writes a correct <head> into each route's HTML file,
  // which is what a crawler receives; once the app has booted no further
  // document is ever requested, so without this every route would keep whatever
  // the first-loaded page said. Both halves read src/lib/routeMeta.ts.
  useRouteMeta()

  return (
  <ChooseChowProvider>
    <AuthProvider>
      <ProjectionRuntimeBridge>
        <Routes>
          <Route path="/" element={<LandingGate />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/plans" element={<PublicPlansPage />} />
          {/* Public and crawlable. A platform that collects delivery addresses
              and processes payments owes these to its customers, and the
              footer, the sitemap and the NDPA all expect to find them here. */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/guidelines" element={<KitchenGuidelinesPage />} />
          <Route path="/kitchen-guidelines" element={<KitchenGuidelinesPage />} />
          {/* Public for a reason: this is the deletion URL declared in Play
              Console, and it has to answer someone who has already uninstalled
              the app or lost access to their account. Behind RequireAuth it
              would fail the very people it exists for. */}
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          {/* Admin + customer-relations console. Unlisted by design: nothing
              links to it and it sits outside the customer/chef shell, so it
              gets no dock, no tab bar, and no role juggling. It carries its own
              operator session; the real gate is the backend's admin-permission
              check on every command it sends. */}
          <Route path="/ops/*" element={<OpsApp />} />
          <Route path="/*" element={<RequireAuth><ChooseChowShell /></RequireAuth>} />
        </Routes>
      </ProjectionRuntimeBridge>
    </AuthProvider>
  </ChooseChowProvider>
  )
}
